import { useMemo } from 'react';

import { useDataTypes } from '../../../hooks/useDataTypes';
import type { StrategyNode } from '../../../types/BaseNode';
import { Tooltip } from '../common/Tooltip';
import { JobProgress } from '../common/JobProgress';

interface Props {
  rootNode: StrategyNode | null;
  isRunning?: boolean;
  jobStatus?: 'queued' | 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled' | null;
  loadingMode?: 'evaluate' | 'quantarc' | 'meepmeep' | null;
  meepMeepJobStage?: string | null;
  meepMeepJobMessage?: string | null;
  meepMeepCurrentDayCount?: number | null;
  meepMeepTotalDayCount?: number | null;
  meepMeepProgressPct?: number | null;
  onRunQuantArc?: () => void;
  onCancelQuantArc?: () => void;
  onCancelMeepMeep?: () => void;
}

function collectSingleTypeIds(node: StrategyNode | null): string[] {
  if (!node) return [];

  const cfg = (node as any).config || {};
  if (node.type === 'Single') {
    const typeId = cfg.typeId ?? cfg.type_id;
    return typeId ? [typeId] : [];
  }

  const children: Array<StrategyNode | null> = [];
  for (const key of ['left', 'right', 'child', 'entry', 'exit', 'target']) {
    children.push(cfg[key] ?? null);
  }
  for (const param of cfg.parameters ?? []) {
    children.push(param ?? null);
  }

  return children.flatMap((child) => collectSingleTypeIds(child));
}

export function TranslationStatusHint({
  rootNode,
  isRunning = false,
  jobStatus = null,
  loadingMode = null,
  meepMeepJobStage = null,
  meepMeepJobMessage = null,
  meepMeepCurrentDayCount = null,
  meepMeepTotalDayCount = null,
  meepMeepProgressPct = null,
  onRunQuantArc,
  onCancelQuantArc,
  onCancelMeepMeep,
}: Props) {
  const { getDataTypeById } = useDataTypes();

  const translationSummary = useMemo(() => {
    if (!rootNode || rootNode.type !== 'PnL') return [];

    const uniqueIds = Array.from(new Set(collectSingleTypeIds(rootNode)));
    return uniqueIds
      .map((typeId) => getDataTypeById(typeId))
      .filter((spec) => spec?.translation)
      .map((spec) => ({
        label: spec?.label || 'Unknown source',
        capabilityLabel: spec?.translation?.label || 'Unknown capability',
        canTranslateNow: Boolean(spec?.translation?.can_translate_now),
        backtestOnly: Boolean(spec?.translation?.backtest_only),
        requiresBundleSnapshot: Boolean(spec?.translation?.requires_bundle_snapshot),
        message: spec?.translation?.message || '',
      }));
  }, [rootNode, getDataTypeById]);

  const blockedSources = translationSummary.filter((source) => !source.canTranslateNow);
  const bundledSources = translationSummary.filter((source) => source.canTranslateNow && source.backtestOnly);
  const canRunInQuantArc = Boolean(rootNode && rootNode.type === 'PnL' && blockedSources.length === 0 && onRunQuantArc);
  
  const isMeepMeepRunning = loadingMode === 'meepmeep';

  if (blockedSources.length === 0 && bundledSources.length === 0 && !canRunInQuantArc && !isMeepMeepRunning) {
    return null;
  }

  const tooltipSections: string[] = [];
  if (blockedSources.length > 0) {
    tooltipSections.push('Blocked for QuantArc translation');
    tooltipSections.push(
      blockedSources
        .map((source) => `${source.label}\n${source.capabilityLabel}\nNot currently translatable in QuantArc.`)
        .join('\n\n'),
    );
  }
  if (bundledSources.length > 0) {
    tooltipSections.push('Bundle-backed only');
    tooltipSections.push(
      bundledSources
        .map(
          (source) =>
            `${source.label}\n${source.capabilityLabel}\nBacktest-only in QuantArc.\nNot available for native QuantArc live alerts or execution.`,
        )
        .join('\n\n'),
    );
  }
  const tooltipContent = tooltipSections.join('\n\n');
  const isBlocking = blockedSources.length > 0;

  const showStatusIcon = blockedSources.length > 0 || bundledSources.length > 0;

  return (
    <div
      style={{
        position: 'absolute',
        top: '14px',
        left: '14px',
        zIndex: 15,
        width: 'fit-content',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      {showStatusIcon && (
        <Tooltip content={tooltipContent} position="right">
          <div
            style={{
              width: '30px',
              height: '30px',
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isBlocking ? 'rgba(231, 76, 60, 0.16)' : 'rgba(243, 156, 18, 0.16)',
              border: isBlocking ? '1px solid rgba(231, 76, 60, 0.4)' : '1px solid rgba(243, 156, 18, 0.4)',
              color: isBlocking ? '#ff8f85' : '#f6c26b',
              fontSize: '0.95rem',
              boxShadow: '0 8px 18px rgba(0, 0, 0, 0.16)',
              cursor: 'help',
            }}
          >
            ⚠
          </div>
        </Tooltip>
      )}

      {canRunInQuantArc && (
        <button
          type="button"
          onClick={isRunning ? onCancelQuantArc : onRunQuantArc}
          disabled={isRunning ? !onCancelQuantArc : false}
          style={{
            padding: '0.55rem 0.85rem',
            borderRadius: '999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.45rem',
            background: isRunning
              ? 'linear-gradient(135deg, rgba(231, 76, 60, 0.22) 0%, rgba(192, 57, 43, 0.18) 100%)'
              : bundledSources.length > 0
                ? 'linear-gradient(135deg, rgba(243, 156, 18, 0.22) 0%, rgba(245, 176, 65, 0.18) 100%)'
                : 'linear-gradient(135deg, rgba(46, 204, 113, 0.22) 0%, rgba(39, 174, 96, 0.18) 100%)',
            border: isRunning
              ? '1px solid rgba(231, 76, 60, 0.45)'
              : bundledSources.length > 0
                ? '1px solid rgba(243, 156, 18, 0.45)'
                : '1px solid rgba(46, 204, 113, 0.45)',
            color: isRunning ? '#ff9b90' : bundledSources.length > 0 ? '#f6c26b' : '#90f0b3',
            fontSize: '0.82rem',
            fontWeight: 700,
            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.16)',
            cursor: isRunning && !onCancelQuantArc ? 'not-allowed' : 'pointer',
            opacity: isRunning && !onCancelQuantArc ? 0.7 : 1,
            transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
          }}
        >
          <span>{isRunning ? '🛑' : '🚀'}</span>
          <span>
            {isRunning
              ? jobStatus === 'cancelling'
                ? 'Cancelling…'
                : 'Cancel QuantArc'
              : 'Run in QuantArc'}
          </span>
        </button>
      )}

      {isMeepMeepRunning && (
        <JobProgress
          loadingStage={meepMeepJobStage}
          loadingMessage={meepMeepJobMessage}
          currentDayCount={meepMeepCurrentDayCount}
          totalDayCount={meepMeepTotalDayCount}
          progressPct={meepMeepProgressPct}
          onCancel={onCancelMeepMeep}
          cancelLabel={jobStatus === 'cancelling' ? 'Cancelling…' : 'Cancel'}
          cancelDisabled={!onCancelMeepMeep || jobStatus === 'cancelling'}
          style={{
            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.25)',
            background: 'rgba(20, 20, 20, 0.85)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        />
      )}
    </div>
  );
}
